/**
 * Cloudflare Turnstile on the public upload page (/u/:token).
 *
 * cloud-api checks a Turnstile token on every `plan` call and refuses one it
 * cannot verify (routes/drop.ts). A token is good for ONE verification, and an
 * upload of many files calls `plan` once per batch, so the uploader asks for a
 * fresh token before each call (`DropUploader`'s token getter) instead of
 * holding one.
 *
 * The widget runs in "execute" mode with "interaction-only" appearance: it
 * stays invisible unless Cloudflare decides it needs the visitor to click.
 * Turnstile is a Cloudflare script on a page whose visitor has no account,
 * which is why the privacy page names it beside Cloudflare's other roles.
 */

import { t } from '../i18n/t';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 60_000;

interface TurnstileApi {
  render(
    el: HTMLElement,
    opts: {
      sitekey: string;
      execution: 'execute';
      appearance: 'interaction-only';
      callback: (token: string) => void;
      'error-callback': () => void;
      'expired-callback': () => void;
    },
  ): string;
  execute(widgetId: string): void;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

function loadScript(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error(t('Turnstile did not load.'))));
    script.onerror = () => {
      loading = null;
      reject(new Error(t('Turnstile did not load.')));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export interface Challenge {
  /** A fresh, unused token. Rejects if the check fails or takes over a minute. */
  token(): Promise<string>;
  dispose(): void;
}

export async function createChallenge(container: HTMLElement, siteKey: string): Promise<Challenge> {
  const api = await loadScript();
  let pending: { resolve: (value: string) => void; reject: (e: Error) => void } | null = null;
  const settle = (fn: (p: NonNullable<typeof pending>) => void) => {
    const p = pending;
    pending = null;
    if (p) fn(p);
  };
  const id = api.render(container, {
    sitekey: siteKey,
    execution: 'execute',
    appearance: 'interaction-only',
    callback: (token) => settle((p) => p.resolve(token)),
    'error-callback': () => settle((p) => p.reject(new Error(t('The check did not pass. Please try again.')))),
    'expired-callback': () => settle((p) => p.reject(new Error(t('The check expired. Please try again.')))),
  });
  return {
    token() {
      return new Promise<string>((resolve, reject) => {
        settle((p) => p.reject(new Error('Superseded.')));
        const timer = setTimeout(
          () => settle((p) => p.reject(new Error(t('The check took too long. Please try again.')))),
          TOKEN_TIMEOUT_MS,
        );
        pending = {
          resolve: (value) => {
            clearTimeout(timer);
            resolve(value);
          },
          reject: (e) => {
            clearTimeout(timer);
            reject(e);
          },
        };
        api.reset(id);
        api.execute(id);
      });
    },
    dispose() {
      settle((p) => p.reject(new Error('Closed.')));
      api.remove(id);
    },
  };
}
